import { useState, useRef } from "react";
import { X, Upload, File } from "lucide-react";
import { useSchoolProfile } from "../../../../app/SchoolProfileContext";
import type { ClassData } from "../../../../lib/api";

interface UploadFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (fileData: any) => void;
  classes: ClassData[];
}

export default function UploadFileModal({
  isOpen,
  onClose,
  onUpload,
  classes,
}: UploadFileModalProps) {
  const { activeProfile } = useSchoolProfile();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [classId, setClassId] = useState<string>("");

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const onButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    setUploading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Content = e.target?.result as string;
      const selectedClass = classes.find((c) => c.id === classId) || null;

      const newFile = {
        name: selectedFile.name,
        type: getFileType(selectedFile.name),
        size: formatBytes(selectedFile.size),
        size_bytes: selectedFile.size,
        date: new Date().toLocaleDateString("pt-BR"),
        class: selectedClass?.name || "Geral",
        class_id: selectedClass?.id || null,
        color: "#1967d2", // Default color
        school_profile_id: activeProfile?.id,
        base64: base64Content, // Adding the base64 content
      };

      onUpload(newFile);
      setUploading(false);
      setSelectedFile(null);
      setClassId("");
      onClose();
    };

    reader.onerror = () => {
      alert("Erro ao ler o arquivo.");
      setUploading(false);
    };

    reader.readAsDataURL(selectedFile);
  };

  const getFileType = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext!)) return "pdf";
    if (["doc", "docx"].includes(ext!)) return "document";
    if (["ppt", "pptx"].includes(ext!)) return "presentation";
    if (["xls", "xlsx"].includes(ext!)) return "spreadsheet";
    if (["jpg", "jpeg", "png", "gif"].includes(ext!)) return "image";
    if (["mp4", "mov", "avi"].includes(ext!)) return "video";
    return "file";
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 border border-(--border-color)">
        <div
          className="px-6 py-4 border-b flex justify-between items-center bg-(--bg-secondary)"
          style={{ borderColor: "var(--border-color)" }}
        >
          <h3 className="text-lg font-semibold text-(--text-primary)">
            Novo Arquivo
          </h3>
          <button
            onClick={onClose}
            className="text-(--text-tertiary) hover:text-(--text-primary) transition-all p-1 hover:bg-(--bg-accent) rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive
                ? "border-(--primary-color) bg-(--bg-accent)"
                : "border-(--border-color) hover:bg-(--bg-secondary)"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleChange}
            />

            {selectedFile ? (
              <div className="flex flex-col items-center">
                <File className="w-12 h-12 text-(--primary-color) mb-3" />
                <p className="font-medium text-(--text-primary) mb-1">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-(--text-tertiary)">
                  {formatBytes(selectedFile.size)}
                </p>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="mt-4 text-sm text-(--danger-color) hover:opacity-80 font-medium"
                >
                  Remover arquivo
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-12 h-12 text-(--text-tertiary) mb-3" />
                <p className="font-medium text-(--text-primary) mb-1">
                  Arraste e solte seu arquivo aqui
                </p>
                <p className="text-sm text-(--text-tertiary) mb-4">ou</p>
                <button
                  onClick={onButtonClick}
                  className="px-4 py-2 bg-(--primary-color) text-white rounded-lg hover:opacity-90 transition font-medium text-sm shadow-sm"
                >
                  Selecionar Arquivo
                </button>
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-(--text-secondary) mb-1">
              Turma
            </label>
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary-color) bg-(--input-bg) text-(--text-primary) transition cursor-pointer"
              style={{ borderColor: "var(--border-color)" }}
            >
              <option value="">Geral (Todas as turmas)</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {uploading && (
            <div className="mt-4 flex items-center justify-center gap-2 text-(--primary-color)">
              <div className="w-4 h-4 border-2 border-(--primary-color) border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">Enviando arquivo...</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-(--bg-secondary) border-t border-(--border-color) flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-(--text-secondary) font-medium hover:bg-(--bg-accent) rounded-lg transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="px-4 py-2 text-white font-medium rounded-lg transition hover:opacity-90 bg-(--primary-color) shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}
